import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { CalendarDays, MapPin, Download, Eye, ChevronRight, XCircle, Loader2, Gift, Share2, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MyBookingsPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null });
  const [cancelling, setCancelling] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchDiscountCodes();
    fetchReferralInfo();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my/discount-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscountCodes(data);
      }
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  };

  const fetchReferralInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my/referral`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferralInfo(data);
      }
    } catch (error) {
      console.error('Error fetching referral info:', error);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadInvoice = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/my/bookings/${bookingId}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${bookingId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const openCancelDialog = async (booking) => {
    setCancelDialog({ open: true, booking });
    setCancelPreview(null);
    setPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${booking.id}/cancel-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCancelPreview(data);
      }
    } catch (error) {
      console.error('Error fetching cancel preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!cancelDialog.booking) return;
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${cancelDialog.booking.id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBookings();
        setCancelDialog({ open: false, booking: null });
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', className: 'status-pending' },
      paid: { label: 'Bezahlt - Warte auf Bestätigung', className: 'status-paid' },
      confirmed: { label: 'Bestätigt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Abgelehnt', className: 'bg-red-50 text-red-700 border-red-200' },
      cancelled: { label: 'Storniert', className: 'status-cancelled' },
      expired: { label: 'Abgelaufen', className: 'status-expired' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-8 lg:py-12 bg-[hsl(var(--surface-2))] min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold mb-2">Meine Buchungen</h1>
            <p className="text-[hsl(var(--muted-foreground))]">
              Willkommen zurück, {user?.name}! Hier sehen Sie alle Ihre Buchungen.
            </p>
          </div>

          {/* Discount Codes & Referral Section */}
          {(discountCodes.length > 0 || (referralInfo && referralInfo.enabled)) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {/* Discount Codes */}
              {discountCodes.length > 0 && (
                <Card className="card-shadow border-amber-200 bg-amber-50/50" data-testid="discount-codes-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-amber-800">Ihre Rabattcodes</h3>
                    </div>
                    <div className="space-y-2">
                      {discountCodes.map((dc) => (
                        <div key={dc.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                          <div>
                            <p className="font-mono font-bold text-amber-900 tracking-wider">{dc.code}</p>
                            <p className="text-xs text-amber-700">
                              {dc.discount_type === 'percent' ? `${dc.discount_value}% Rabatt` : `CHF ${dc.discount_value} Rabatt`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(dc.code, dc.id)}
                            className="text-amber-700 hover:bg-amber-100"
                            data-testid={`copy-discount-${dc.id}`}
                          >
                            {copiedCode === dc.id ? <span className="text-xs text-emerald-600">Kopiert!</span> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Referral Program */}
              {referralInfo && referralInfo.enabled && (
                <Card className="card-shadow border-blue-200 bg-blue-50/50" data-testid="referral-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Share2 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-blue-800">Freunde einladen</h3>
                    </div>
                    <p className="text-sm text-blue-700 mb-3">
                      Teilen Sie Ihren Empfehlungscode mit Freunden.
                      {referralInfo.friend_type === 'percent'
                        ? ` Ihr Freund erhält ${referralInfo.friend_value}% Rabatt`
                        : ` Ihr Freund erhält CHF ${referralInfo.friend_value} Rabatt`}
                      {referralInfo.reward_type === 'percent'
                        ? ` und Sie erhalten ${referralInfo.reward_value}% auf Ihre nächste Buchung!`
                        : ` und Sie erhalten CHF ${referralInfo.reward_value} auf Ihre nächste Buchung!`}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white rounded-lg p-3 border border-blue-200 font-mono font-bold text-blue-900 text-center tracking-widest text-lg">
                        {referralInfo.referral_code}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(referralInfo.referral_code, 'referral')}
                        className="text-blue-700 hover:bg-blue-100"
                        data-testid="copy-referral-code"
                      >
                        {copiedCode === 'referral' ? <span className="text-xs text-emerald-600">Kopiert!</span> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    {referralInfo.referral_count > 0 && (
                      <p className="text-xs text-blue-600 mt-2">{referralInfo.referral_count} Freunde bereits eingeladen</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="mb-6">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="all">Alle ({bookings.length})</TabsTrigger>
                <TabsTrigger value="confirmed">Bestätigt</TabsTrigger>
                <TabsTrigger value="paid">Bezahlt</TabsTrigger>
                <TabsTrigger value="pending">Ausstehend</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Bookings List */}
          {filteredBookings.length === 0 ? (
            <Card className="card-shadow">
              <CardContent className="p-12 text-center">
                <CalendarDays className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))] opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Keine Buchungen</h3>
                <p className="text-[hsl(var(--muted-foreground))] mb-4">
                  Sie haben noch keine Buchungen. Finden Sie jetzt den perfekten Standort für Ihre Werbung!
                </p>
                <Link to="/locations">
                  <Button>Standorte entdecken</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredBookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card className="card-shadow hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Left Side - Booking Info */}
                        <div className="flex-1">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-[hsl(var(--primary)/0.1)] rounded-xl flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-6 h-6 text-[hsl(var(--primary))]" />
                            </div>
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className="font-semibold text-lg">{booking.location?.name}</h3>
                                {getStatusBadge(booking.status)}
                              </div>
                              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                                {booking.location?.address}
                              </p>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="flex items-center">
                                  <CalendarDays className="w-4 h-4 mr-1 text-[hsl(var(--muted-foreground))]" />
                                  {format(new Date(booking.start_date), 'dd.MM.yyyy', { locale: de })}
                                  {' - '}
                                  {format(new Date(booking.end_date), 'dd.MM.yyyy', { locale: de })}
                                </span>
                              </div>
                              {booking.invoice_number && (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                                  Rechnungsnr.: {booking.invoice_number}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Side - Price & Actions */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="text-right">
                            <p className="text-2xl font-semibold">CHF {formatPrice(booking.total_price)}</p>
                            {booking.services_price > 0 && (
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                inkl. Zusatzservices
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {(booking.status === 'paid' || booking.status === 'confirmed') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => downloadInvoice(booking.id)}
                                data-testid={`download-invoice-${booking.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Rechnung
                              </Button>
                            )}
                            <Link to={`/my-bookings/${booking.id}`}>
                              <Button size="sm" data-testid={`view-booking-${booking.id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                Details
                              </Button>
                            </Link>
                            {(booking.status === 'pending' || booking.status === 'paid') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                onClick={() => openCancelDialog(booking)}
                                data-testid={`cancel-booking-${booking.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Stornieren
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Cancel Booking Dialog */}
          <Dialog open={cancelDialog.open} onOpenChange={(open) => { if (!open) { setCancelDialog({ open: false, booking: null }); setCancelPreview(null); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Buchung stornieren</DialogTitle>
                <DialogDescription>
                  Möchten Sie die Buchung für "{cancelDialog.booking?.location?.name}" wirklich stornieren?
                </DialogDescription>
              </DialogHeader>

              {previewLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" />
                  <span className="ml-2 text-sm text-[hsl(var(--muted-foreground))]">Erstattung wird berechnet...</span>
                </div>
              ) : cancelPreview && cancelDialog.booking?.status === 'paid' ? (
                <div className="rounded-lg border border-[hsl(var(--border))] p-4 space-y-2" data-testid="cancel-refund-preview">
                  <div className="flex justify-between text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">Buchungsbetrag:</span>
                    <span className="font-medium">CHF {formatPrice(cancelPreview.total_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">Tage bis Buchungsbeginn:</span>
                    <span className="font-medium">{cancelPreview.days_until_start}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-[hsl(var(--border))]">
                    <span className="text-[hsl(var(--muted-foreground))]">Erstattung:</span>
                    <span className={`font-semibold ${cancelPreview.refund_percent > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {cancelPreview.refund_percent}% (CHF {formatPrice(cancelPreview.refund_amount)})
                    </span>
                  </div>

                  {/* Cancellation tiers visualization */}
                  {cancelPreview.tiers && cancelPreview.tiers.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                      <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] mb-2">Stornierungsstaffel:</p>
                      <div className="space-y-1.5">
                        {cancelPreview.tiers.map((tier, i) => {
                          const isActive = cancelPreview.days_until_start >= tier.days_before &&
                            (i === 0 || cancelPreview.days_until_start < cancelPreview.tiers[i - 1].days_before);
                          return (
                            <div
                              key={i}
                              className={`flex items-center justify-between text-xs p-1.5 rounded ${
                                isActive ? 'bg-[hsl(var(--primary)/0.08)] font-semibold' : ''
                              }`}
                              data-testid={`tier-${tier.days_before}`}
                            >
                              <span className="flex items-center gap-1.5">
                                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />}
                                Ab {tier.days_before} Tage vorher
                              </span>
                              <span className={tier.refund_percent === 100 ? 'text-emerald-600' : tier.refund_percent === 0 ? 'text-red-600' : 'text-amber-600'}>
                                {tier.refund_percent}% Erstattung
                              </span>
                            </div>
                          );
                        })}
                        {/* Show 0% tier if not in tiers */}
                        {cancelPreview.tiers.every(t => t.refund_percent > 0) && (
                          <div className={`flex items-center justify-between text-xs p-1.5 rounded ${
                            cancelPreview.refund_percent === 0 ? 'bg-red-50 font-semibold' : ''
                          }`}>
                            <span className="flex items-center gap-1.5">
                              {cancelPreview.refund_percent === 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                              Weniger als {Math.min(...cancelPreview.tiers.map(t => t.days_before))} Tage
                            </span>
                            <span className="text-red-600">0% Erstattung</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {cancelPreview.refund_percent === 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Gemäss Stornierungsrichtlinien ist leider keine Erstattung mehr möglich.
                    </p>
                  )}
                  {cancelPreview.refund_percent > 0 && cancelPreview.refund_percent < 100 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Aufgrund der Stornierungsfrist wird nur eine Teilerstattung gewährt.
                    </p>
                  )}
                </div>
              ) : null}

              <DialogFooter>
                <Button variant="outline" onClick={() => { setCancelDialog({ open: false, booking: null }); setCancelPreview(null); }}>
                  Abbrechen
                </Button>
                <Button
                  variant="destructive"
                  onClick={cancelBooking}
                  disabled={cancelling}
                  data-testid="confirm-cancel-booking-button"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Ja, stornieren
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </motion.div>
      </div>
    </div>
  );
};

export default MyBookingsPage;
